import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { MessageSquare, Search, Send, ArrowLeft, ExternalLink, Phone } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { api, SmsConversation, SmsLogEntry } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

function relativeTime(iso: string) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
}

function formatMsgTime(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  } catch {
    return "";
  }
}

function formatMsgDate(iso: string) {
  try {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function Messages() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState("");
  const [replySending, setReplySending] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);

  const { data: conversations = [], isLoading } = useQuery<SmsConversation[]>({
    queryKey: ["sms-conversations"],
    queryFn: () => api.getSmsConversations(),
    refetchInterval: 15_000,
  });

  const { data: thread = [] } = useQuery<SmsLogEntry[]>({
    queryKey: ["sms-log", selectedAccountId],
    queryFn: () => api.getSmsLog(selectedAccountId!),
    enabled: !!selectedAccountId,
    refetchInterval: 15_000,
  });

  // Fetch contacts for the selected account so we can pick an SMS-enabled contact for replies
  const { data: contacts = [] } = useQuery({
    queryKey: ["contacts", selectedAccountId],
    queryFn: () => api.getContacts({ accountId: selectedAccountId! }),
    enabled: !!selectedAccountId,
  });

  const smsContacts = contacts.filter((c: any) => c.sms_ok && (c.mobile || c.phone));
  const [replyContactId, setReplyContactId] = useState<string>("");

  // Auto-select first SMS contact when account changes
  useEffect(() => {
    if (smsContacts.length > 0) {
      setReplyContactId(smsContacts[0].id);
    } else {
      setReplyContactId("");
    }
  }, [selectedAccountId, smsContacts.length]);

  // Mark conversation as read when opened
  useEffect(() => {
    if (!selectedAccountId) return;
    api.markSmsConversationRead(selectedAccountId).then(() => {
      queryClient.invalidateQueries({ queryKey: ["sms-conversations"] });
    });
  }, [selectedAccountId, queryClient]);

  // Scroll to bottom of thread when new messages arrive
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [thread.length]);

  const selectedConvo = conversations.find((c) => c.account_id === selectedAccountId);

  const filtered = search.trim()
    ? conversations.filter((c) =>
        c.account_name.toLowerCase().includes(search.toLowerCase()) ||
        (c.contact_name ?? "").toLowerCase().includes(search.toLowerCase())
      )
    : conversations;

  async function sendReply() {
    if (!replyBody.trim() || !selectedAccountId) return;
    const contact = smsContacts.find((c: any) => c.id === replyContactId) ?? smsContacts[0];
    if (!contact) {
      toast({ title: "No SMS-enabled contact found", description: "Enable SMS for a contact on their profile first.", variant: "destructive" });
      return;
    }
    const toNumber = contact.mobile || contact.phone;
    if (!toNumber) {
      toast({ title: "Contact has no phone number", variant: "destructive" });
      return;
    }
    setReplySending(true);
    try {
      await api.sendSmsFromProfile({ to: toNumber, body: replyBody.trim(), account_id: selectedAccountId, contact_id: contact.id });
      setReplyBody("");
      queryClient.invalidateQueries({ queryKey: ["sms-log", selectedAccountId] });
      queryClient.invalidateQueries({ queryKey: ["sms-conversations"] });
    } catch (err: any) {
      toast({ title: "Failed to send", description: err.message, variant: "destructive" });
    } finally {
      setReplySending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendReply();
    }
  }

  // Group thread messages by date
  function groupByDate(msgs: SmsLogEntry[]) {
    const groups: { date: string; msgs: SmsLogEntry[] }[] = [];
    for (const msg of msgs) {
      const d = formatMsgDate(msg.created_at);
      const last = groups[groups.length - 1];
      if (last && last.date === d) {
        last.msgs.push(msg);
      } else {
        groups.push({ date: d, msgs: [msg] });
      }
    }
    return groups;
  }

  return (
    <Layout>
      <div className="flex h-[calc(100vh-4rem)] overflow-hidden -m-4 md:-m-6">
        {/* ── Left panel: conversation list ── */}
        <div className="w-80 shrink-0 flex flex-col border-r border-border bg-background">
          <div className="p-3 border-b border-border">
            <h1 className="text-base font-semibold mb-2 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-primary" />
              Text Messages
            </h1>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Search clients…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="p-4 text-sm text-muted-foreground text-center">Loading…</div>
            )}
            {!isLoading && filtered.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground space-y-2">
                <MessageSquare className="w-8 h-8 mx-auto opacity-20" />
                <p>{search ? "No results." : "No SMS conversations yet."}</p>
                {!search && (
                  <p className="text-xs">
                    Enable SMS on a contact from their{" "}
                    <Link href="/accounts" className="text-primary underline">client profile</Link>.
                  </p>
                )}
              </div>
            )}
            {filtered.map((convo) => {
              const isSelected = convo.account_id === selectedAccountId;
              const isInbound = convo.last_message_direction === "inbound";
              return (
                <button
                  key={convo.account_id}
                  type="button"
                  onClick={() => setSelectedAccountId(convo.account_id)}
                  className={cn(
                    "w-full text-left px-4 py-3 border-b border-border/50 hover:bg-accent/40 transition-colors group",
                    isSelected && "bg-blue-50 border-blue-100"
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className={cn("text-sm font-semibold truncate", convo.unread_count > 0 && "text-foreground")}>
                          {convo.account_name}
                        </span>
                        {convo.unread_count > 0 && (
                          <span className="shrink-0 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                            {convo.unread_count}
                          </span>
                        )}
                      </div>
                      {convo.contact_name && (
                        <p className="text-[11px] text-muted-foreground truncate">{convo.contact_name}</p>
                      )}
                      <p className={cn(
                        "text-xs mt-0.5 truncate",
                        convo.unread_count > 0 ? "text-foreground font-medium" : "text-muted-foreground"
                      )}>
                        {isInbound ? "" : "You: "}{convo.last_message_body}
                      </p>
                    </div>
                    <div className="shrink-0 text-[10px] text-muted-foreground text-right">
                      <span>{relativeTime(convo.last_message_at)}</span>
                      <div className={cn("text-[9px] mt-0.5", isInbound ? "text-amber-600 font-medium" : "text-muted-foreground/60")}>
                        {isInbound ? "← client" : "→ sent"}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Right panel: thread + compose ── */}
        <div className="flex-1 flex flex-col bg-background min-w-0">
          {!selectedAccountId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground space-y-3">
              <MessageSquare className="w-12 h-12 opacity-20" />
              <p className="text-sm">Select a conversation to view messages</p>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setSelectedAccountId(null)}
                    className="md:hidden p-1 rounded hover:bg-accent"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <div>
                    <p className="text-sm font-semibold">{selectedConvo?.account_name ?? "—"}</p>
                    {selectedConvo?.contact_name && (
                      <p className="text-xs text-muted-foreground">{selectedConvo.contact_name}</p>
                    )}
                  </div>
                </div>
                <Link href={`/accounts/${selectedAccountId}`}>
                  <Button variant="ghost" size="sm" className="text-xs gap-1 text-muted-foreground hover:text-foreground">
                    <ExternalLink className="w-3 h-3" />
                    Open profile
                  </Button>
                </Link>
              </div>

              {/* Message thread */}
              <div ref={threadRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                {thread.length === 0 && (
                  <div className="text-center text-sm text-muted-foreground py-8">No messages yet.</div>
                )}
                {groupByDate(thread).map(({ date, msgs }) => (
                  <div key={date}>
                    <div className="flex items-center gap-3 my-3">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-[11px] text-muted-foreground font-medium shrink-0">{date}</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                    <div className="space-y-1.5">
                      {msgs.map((msg) => {
                        const isOut = msg.direction === "outbound";
                        return (
                          <div key={msg.id} className={cn("flex", isOut ? "justify-end" : "justify-start")}>
                            <div className={cn(
                              "max-w-[70%] rounded-2xl px-3.5 py-2 text-sm",
                              isOut
                                ? "bg-primary text-primary-foreground rounded-tr-sm"
                                : "bg-muted text-foreground rounded-tl-sm"
                            )}>
                              <p className="leading-snug whitespace-pre-wrap break-words">{msg.body}</p>
                              <p className={cn(
                                "text-[10px] mt-1 text-right",
                                isOut ? "text-primary-foreground/60" : "text-muted-foreground"
                              )}>
                                {formatMsgTime(msg.created_at)}
                                {isOut && msg.sent_by_name && ` · ${msg.sent_by_name}`}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply bar */}
              <div className="px-4 py-3 border-t border-border bg-background space-y-2">
                {smsContacts.length === 0 ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <Phone className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    <span>No SMS-enabled contacts. </span>
                    <Link href={`/accounts/${selectedAccountId}`} className="text-primary underline">
                      Enable SMS on a contact
                    </Link>
                    <span> to reply.</span>
                  </div>
                ) : (
                  <div className="flex items-end gap-2">
                    {smsContacts.length > 1 && (
                      <select
                        value={replyContactId}
                        onChange={(e) => setReplyContactId(e.target.value)}
                        className="shrink-0 text-xs border border-input rounded-md px-2 py-1.5 bg-background h-9"
                      >
                        {smsContacts.map((c: any) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    )}
                    <Textarea
                      placeholder="Type a message… (Enter to send)"
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      onKeyDown={handleKeyDown}
                      rows={1}
                      className="flex-1 resize-none text-sm min-h-9 py-2"
                      maxLength={1600}
                    />
                    <Button
                      onClick={sendReply}
                      disabled={!replyBody.trim() || replySending}
                      size="sm"
                      className="shrink-0 h-9"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
                {replyBody.length > 1200 && (
                  <p className="text-[10px] text-muted-foreground text-right">{replyBody.length}/1600</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
